@file:OptIn(ExperimentalMaterial3Api::class, ExperimentalFoundationApi::class)

package com.example.monetix.ui.dialogs

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.snapping.rememberSnapFlingBehavior
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDownward
import androidx.compose.material.icons.filled.CalendarMonth
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material3.*
import com.example.monetix.ui.components.MonetixTextField as OutlinedTextField
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.compose.ui.window.PopupProperties

import com.example.monetix.db.MonetixDatabase
import com.example.monetix.db.BudgetEntity
import com.example.monetix.db.CategoryEntity
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.todayIn
import kotlin.math.roundToInt

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun BudgetWheelPicker(
    items: List<Int>,
    initialValue: Int,
    onValueChanged: (Int) -> Unit,
    modifier: Modifier = Modifier,
    labelExtractor: (Int) -> String = { it.toString() }
) {
    val initialIndex = items.indexOf(initialValue).coerceAtLeast(0)
    val listState = rememberLazyListState(initialFirstVisibleItemIndex = initialIndex)
    val flingBehavior = rememberSnapFlingBehavior(lazyListState = listState)
    val itemHeight = 48.dp

    LaunchedEffect(listState.isScrollInProgress) {
        if (!listState.isScrollInProgress && listState.layoutInfo.visibleItemsInfo.isNotEmpty()) {
            val centerOffset = listState.layoutInfo.viewportEndOffset / 2
            val closestItem = listState.layoutInfo.visibleItemsInfo.minByOrNull {
                kotlin.math.abs(it.offset + (it.size / 2) - centerOffset)
            }
            if (closestItem != null) {
                val dataIndex = closestItem.index - 1
                if (dataIndex in items.indices) {
                    onValueChanged(items[dataIndex])
                    listState.animateScrollToItem(dataIndex)
                }
            }
        }
    }

    Box(modifier = modifier.height(itemHeight * 3), contentAlignment = Alignment.Center) {
        LazyColumn(
            state = listState,
            flingBehavior = flingBehavior,
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.fillMaxSize()
        ) {
            item { Spacer(modifier = Modifier.height(itemHeight)) }
            items(items.size) { index ->
                Box(modifier = Modifier.height(itemHeight).fillMaxWidth(), contentAlignment = Alignment.Center) {
                    Text(
                        text = labelExtractor(items[index]),
                        style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold, fontSize = 24.sp),
                        color = MaterialTheme.colorScheme.onSurface
                    )
                }
            }
            item { Spacer(modifier = Modifier.height(itemHeight)) }
        }
        HorizontalDivider(modifier = Modifier.align(Alignment.TopCenter).padding(top = itemHeight), color = MaterialTheme.colorScheme.primary, thickness = 2.dp)
        HorizontalDivider(modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = itemHeight), color = MaterialTheme.colorScheme.primary, thickness = 2.dp)
    }
}

@Composable
private fun BudgetStableTextField(
    value: TextFieldValue,
    onValueChange: (TextFieldValue) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    trailingIcon: @Composable (() -> Unit)? = null,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    keyboardType: KeyboardType = KeyboardType.Text
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(label, style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0)) },
        maxLines = 1,
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(14.dp),
        trailingIcon = trailingIcon,
        interactionSource = interactionSource,
        keyboardOptions = KeyboardOptions(keyboardType = keyboardType, autoCorrectEnabled = false),
        colors = OutlinedTextFieldDefaults.colors(
            focusedTextColor = Color.White,
            unfocusedTextColor = Color.White,
            focusedContainerColor = Color.Black.copy(alpha = 0.35f),
            unfocusedContainerColor = Color.Black.copy(alpha = 0.25f),
            focusedBorderColor = Color.White.copy(alpha = 0.40f),
            unfocusedBorderColor = Color.White.copy(alpha = 0.20f),
            focusedLabelColor = Color.White,
            unfocusedLabelColor = Color(0xFFA0A0A0)
        )
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BudgetDialog(
    db: MonetixDatabase,
    initial: BudgetEntity? = null,
    categories: List<CategoryEntity>,
    onDismiss: () -> Unit,
    onSave: (BudgetEntity) -> Unit,
    onDelete: ((String) -> Unit)? = null,
    onCategoryCreate: ((CategoryEntity) -> Unit)? = null
) {
    val brandPrimary = MaterialTheme.colorScheme.primary
    val scope = rememberCoroutineScope()
    val nowMs = Clock.System.now().toEpochMilliseconds()

    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
    val initialDate = initial?.let { Instant.fromEpochMilliseconds(it.createdAtEpochMs).toLocalDateTime(TimeZone.currentSystemDefault()).date } ?: today
    val initialDateStr = "${initialDate.dayOfMonth.toString().padStart(2, '0')}/${initialDate.monthNumber.toString().padStart(2, '0')}/${initialDate.year}"

    var dateTextFieldValue by remember { mutableStateOf(TextFieldValue(text = initialDateStr, selection = TextRange(initialDateStr.length))) }
    var selectedEpochMs by remember { mutableStateOf(initial?.createdAtEpochMs ?: nowMs) }
    var showDatePicker by remember { mutableStateOf(false) }
    val datePickerState = rememberDatePickerState(initialSelectedDateMillis = selectedEpochMs)

    val dateInteractionSource = remember { MutableInteractionSource() }
    val isDateFocused by dateInteractionSource.collectIsFocusedAsState()

    LaunchedEffect(isDateFocused) {
        if (isDateFocused) {
            dateTextFieldValue = TextFieldValue(text = "", selection = TextRange.Zero)
        }
    }

    val onDateValueChange: (TextFieldValue) -> Unit = let@{ newVal ->
        val oldText = dateTextFieldValue.text
        val newText = newVal.text
        if (newText.length < oldText.length) {
            dateTextFieldValue = newVal
            return@let
        }
        val clean = newText.replace("/", "").filter { it.isDigit() }
        val formatted = buildString {
            for (i in clean.indices) {
                append(clean[i])
                if ((i == 1 || i == 3) && i < 7) append("/")
            }
        }.take(10)
        dateTextFieldValue = TextFieldValue(text = formatted, selection = TextRange(formatted.length))
    }

    val initialAmountStr = if (initial != null) {
        val e = initial.limitCents / 100
        val c = kotlin.math.abs(initial.limitCents % 100).toString().padStart(2, '0')
        "$e,$c"
    } else ""

    var amountTextFieldValue by remember { mutableStateOf(TextFieldValue(text = initialAmountStr, selection = TextRange(initialAmountStr.length))) }
    var showAmountPicker by remember { mutableStateOf(false) }
    val amountInteractionSource = remember { MutableInteractionSource() }
    val isAmountFocused by amountInteractionSource.collectIsFocusedAsState()

    LaunchedEffect(isAmountFocused) {
        if (isAmountFocused) {
            amountTextFieldValue = TextFieldValue(text = "", selection = TextRange.Zero)
        }
    }

    val initialCategoryName = categories.firstOrNull { it.id == initial?.categoryId }?.name ?: ""
    var categorySearchText by remember(initial?.categoryId) { mutableStateOf(initialCategoryName) }
    var categoryMenuOpen by remember { mutableStateOf(false) }
    val catInteractionSource = remember { MutableInteractionSource() }
    val isCatFocused by catInteractionSource.collectIsFocusedAsState()

    var showDeleteConfirm by remember { mutableStateOf(false) }

    LaunchedEffect(isCatFocused) {
        if (isCatFocused) {
            if (categorySearchText == initialCategoryName) categorySearchText = ""
            categoryMenuOpen = true
        }
    }

    val bgGradient = remember {
        Brush.verticalGradient(
            colors = listOf(
                Color(0xFFB45309).copy(alpha = 0.75f),
                Color(0xFF78350F),
                Color(0xFF0F172A)
            )
        )
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnClickOutside = true, dismissOnBackPress = true)
    ) {
        Surface(modifier = Modifier.fillMaxSize(), color = Color.Transparent) {
            Box(modifier = Modifier.fillMaxSize().background(bgGradient)) {
                Column(modifier = Modifier.fillMaxSize().systemBarsPadding().imePadding()) {
                
                // HEADER STILE REVOLUT
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 16.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    IconButton(
                        onClick = onDismiss,
                        modifier = Modifier.size(40.dp).background(Color.White.copy(alpha = 0.10f), CircleShape)
                    ) {
                        Icon(Icons.Default.ArrowBack, contentDescription = "Indietro", tint = Color.White)
                    }
                    if (initial != null && onDelete != null) {
                        IconButton(
                            onClick = { showDeleteConfirm = true },
                            modifier = Modifier.size(40.dp).background(Color.White.copy(alpha = 0.10f),
                                CircleShape
                            )
                        ) {
                            Icon(Icons.Outlined.Delete, contentDescription = "Elimina", tint = MaterialTheme.colorScheme.error)
                        }
                    }
                }

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f)
                        .verticalScroll(rememberScrollState())
                        .padding(horizontal = 24.dp, vertical = 8.dp)
                ) {
                    Text(
                        text = if (initial == null) "Nuovo Budget" else "Modifica Budget",
                        style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                        color = Color.White
                    )
                    Spacer(Modifier.height(24.dp))
                    
                    BudgetStableTextField(
                        value = dateTextFieldValue,
                        onValueChange = onDateValueChange,
                        label = "Data Riferimento (gg/mm/aaaa)",
                        interactionSource = dateInteractionSource,
                        keyboardType = KeyboardType.Number,
                        trailingIcon = { IconButton(onClick = { showDatePicker = true }) { Icon(Icons.Default.CalendarMonth, null, tint = Color.White) } }
                    )

                    Spacer(Modifier.height(16.dp))

                    Box {
                        OutlinedTextField(
                            value = categorySearchText,
                            onValueChange = { categorySearchText = it; categoryMenuOpen = true },
                            label = { Text("Categoria", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0)) },
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(14.dp),
                            interactionSource = catInteractionSource,
                            trailingIcon = { IconButton(onClick = { categoryMenuOpen = !categoryMenuOpen }) { Icon(if (categoryMenuOpen) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown, null, tint = Color.White) } },
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedTextColor = Color.White,
                                unfocusedTextColor = Color.White,
                                focusedContainerColor = Color.Black.copy(alpha = 0.35f),
                                unfocusedContainerColor = Color.Black.copy(alpha = 0.25f),
                                focusedBorderColor = Color.White.copy(alpha = 0.40f),
                                unfocusedBorderColor = Color.White.copy(alpha = 0.20f),
                                focusedLabelColor = Color.White,
                                unfocusedLabelColor = Color(0xFFA0A0A0)
                            )
                        )
                        DropdownMenu(
                            expanded = categoryMenuOpen,
                            onDismissRequest = { categoryMenuOpen = false },
                            containerColor = Color(0xFF1C1C1E),
                            modifier = Modifier.fillMaxWidth(0.8f),
                            properties = PopupProperties(focusable = false)
                        ) {
                            val filtered = categories.filter { !it.isDeleted && it.name.contains(categorySearchText, ignoreCase = true) }
                            if (categorySearchText.isNotBlank() && filtered.none { it.name.equals(categorySearchText, ignoreCase = true) }) {
                                DropdownMenuItem(
                                    text = { Text("+ Crea \"${categorySearchText.uppercase()}\"", color = Color(0xFF60A5FA), fontWeight = FontWeight.Bold) },
                                    colors = MenuDefaults.itemColors(textColor = Color(0xFF60A5FA)),
                                    onClick = {
                                        val catTextClean = categorySearchText.trim().uppercase()
                                        val norm = catTextClean.lowercase().replace("\\s+".toRegex(), "_").replace("[^a-z0-9_]+".toRegex(), "")
                                        val newId = "cat_$norm"
                                        onCategoryCreate?.invoke(CategoryEntity(newId, catTextClean, false, nowMs, nowMs))
                                        categorySearchText = catTextClean; categoryMenuOpen = false
                                    }
                                )
                            }
                            filtered.forEach { c ->
                                DropdownMenuItem(
                                    text = { Text(c.name, color = Color.White) },
                                    trailingIcon = {
                                        IconButton(onClick = {
                                            scope.launch { db.categoryQueries.upsert(c.id, c.name, true, c.createdAtEpochMs, Clock.System.now().toEpochMilliseconds()) }
                                        }) { Icon(Icons.Outlined.Delete, "Elimina", tint = MaterialTheme.colorScheme.error) }
                                    },
                                    onClick = { categorySearchText = c.name; categoryMenuOpen = false }
                                )
                            }
                        }
                    }

                    Spacer(Modifier.height(16.dp))

                    BudgetStableTextField(
                        value = amountTextFieldValue,
                        onValueChange = { amountTextFieldValue = it },
                        label = "Limite Massimo (€)",
                        keyboardType = KeyboardType.Decimal,
                        interactionSource = amountInteractionSource,
                        trailingIcon = { IconButton(onClick = { showAmountPicker = true }) { Icon(Icons.Default.MoreVert, contentDescription = "Rotelle", tint = Color.White) } }
                    )
                    
                    Spacer(Modifier.height(100.dp)) // padding for fab
                }
            }

            // FAB SALVA
            val isFormValid = categorySearchText.isNotBlank() && amountTextFieldValue.text.isNotBlank()
            Box(modifier = Modifier.fillMaxSize().padding(24.dp), contentAlignment = Alignment.BottomEnd) {
                FloatingActionButton(
                    onClick = {
                        if (isFormValid) {
                            val limitCents = try {
                                val parts = amountTextFieldValue.text.replace(",", ".").split(".")
                                val e = parts.getOrNull(0)?.toLongOrNull() ?: 0L
                                val c = parts.getOrNull(1)?.padEnd(2, '0')?.take(2)?.toLongOrNull() ?: 0L
                                (e * 100L) + c
                            } catch (e: Exception) { 0L }

                            val catTextClean = categorySearchText.trim().uppercase()
                            val existing = categories.firstOrNull { !it.isDeleted && it.name.equals(catTextClean, ignoreCase = true) }

                            val finalId = existing?.id ?: if (catTextClean.isNotEmpty()) {
                                val norm = catTextClean.lowercase().replace("\\s+".toRegex(), "_").replace("[^a-z0-9_]+".toRegex(), "")
                                val newId = "cat_$norm"
                                onCategoryCreate?.invoke(CategoryEntity(newId, catTextClean, false, nowMs, nowMs))
                                newId
                            } else null

                            if (finalId != null && limitCents > 0L) {
                                onSave(BudgetEntity(categoryId = finalId, limitCents = limitCents, isDeleted = false, createdAtEpochMs = selectedEpochMs, updatedAtEpochMs = nowMs))
                            }
                        }
                    },
                    shape = CircleShape,
                    containerColor = if (isFormValid) Color(0xFFF59E0B) else Color(0xFF2C2C2E),
                    contentColor = if (isFormValid) Color.White else Color(0xFFA0A0A0)
                ) {
                    Icon(Icons.Default.ArrowDownward, contentDescription = "Salva", modifier = Modifier.size(24.dp))
                }
            }
        }
    }
    }

    if (showDeleteConfirm && initial != null && onDelete != null) {
        AlertDialog(
            onDismissRequest = { showDeleteConfirm = false },
            containerColor = Color.White.copy(alpha = 0.12f),
            shape = RoundedCornerShape(28.dp),
            title = { Text("Elimina Budget", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color.White) },
            text = { Text("Sei sicuro di voler eliminare questo limite di budget?", style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0)) },
            confirmButton = {
                Button(onClick = { onDelete(initial.categoryId); showDeleteConfirm = false }, colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error, contentColor = Color.White)) { Text("ELIMINA", fontWeight = FontWeight.Bold) }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteConfirm = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            }
        )
    }

    if (showAmountPicker) {
        val parsedInitial = amountTextFieldValue.text.replace(',', '.').toDoubleOrNull() ?: 0.0
        var tempEuro by remember { mutableStateOf(kotlin.math.floor(parsedInitial).toInt().coerceIn(0, 9999)) }
        var tempCent by remember { mutableStateOf(((parsedInitial - kotlin.math.floor(parsedInitial)) * 100).roundToInt().coerceIn(0, 99)) }

        AlertDialog(
            onDismissRequest = { showAmountPicker = false },
            containerColor = Color.White.copy(alpha = 0.12f),
            shape = RoundedCornerShape(28.dp),
            title = { Text("Limite Budget", fontWeight = FontWeight.Bold, color = Color(0xFFF59E0B)) },
            text = {
                Column(modifier = Modifier.fillMaxWidth().padding(top = 8.dp)) {
                    Text(
                        text = "$tempEuro,${tempCent.toString().padStart(2, '0')} €",
                        style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.ExtraBold),
                        color = Color.White,
                        modifier = Modifier.align(Alignment.CenterHorizontally)
                    )
                    Spacer(Modifier.height(24.dp))
                    Row(
                        modifier = Modifier.fillMaxWidth().height(160.dp),
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        BudgetWheelPicker(items = (0..5000).toList(), initialValue = tempEuro, onValueChanged = { tempEuro = it }, modifier = Modifier.weight(1f))
                        Text(",", style = MaterialTheme.typography.headlineLarge, color = Color.White, modifier = Modifier.padding(horizontal = 16.dp))
                        BudgetWheelPicker(items = (0..99).toList(), initialValue = tempCent, onValueChanged = { tempCent = it }, modifier = Modifier.weight(1f), labelExtractor = { it.toString().padStart(2, '0') })
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = {
                    val newAmt = "$tempEuro,${tempCent.toString().padStart(2, '0')}"
                    amountTextFieldValue = TextFieldValue(text = newAmt, selection = TextRange(newAmt.length))
                    showAmountPicker = false
                }) { Text("CONFERMA", color = Color(0xFFF59E0B), fontWeight = FontWeight.Bold) }
            },
            dismissButton = {
                TextButton(onClick = { showAmountPicker = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            }
        )
    }

    if (showDatePicker) {
        DatePickerDialog(
            onDismissRequest = { showDatePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    datePickerState.selectedDateMillis?.let { millis ->
                        selectedEpochMs = millis
                        val d = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.currentSystemDefault()).date
                        val newDateStr = "${d.dayOfMonth.toString().padStart(2, '0')}/${d.monthNumber.toString().padStart(2, '0')}/${d.year}"
                        dateTextFieldValue = TextFieldValue(text = newDateStr, selection = TextRange(newDateStr.length))
                    }
                    showDatePicker = false
                }) { Text("OK") }
            }
        ) { DatePicker(state = datePickerState) }
    }
}